import "bootstrap/dist/css/bootstrap.css";
import "bootstrap";
import React, {FC, useEffect} from "react";
import ProfileWizard from "./Profile/ProfileWizard";
import {getUser, postUser} from "../lib/api";
import {BrowserRouter as Router, Redirect, Route, Switch} from "react-router-dom"
import Home from "./Home";
import ErrorMessage from "./Misc/ErrorMessage";
import TeamView from "./Team/TeamView";
import OrgView from "./Org/OrgView";
import LoadingScreen from "./Misc/LoadingScreen";
import LinkDeviceWrapper from "./Profile/LinkDeviceWrapper";
import TermsOfUse from "./Misc/TermsOfUse";
import PrivacyPolicy from "./Misc/PrivacyPolicy";
import Library from "./Library/Library";
import Performances from "./Performances/Performances";
import AppIntegrations from "./Profile/AppIntegrations";
import Admin from "./Admin/Admin";
import QuestionAnswer from "./Misc/QuestionAnswer";
import SketchfabRedirect from "./Profile/SketchfabRedirect";
import TeamAssets from "./Team/TeamAssets";
import NftTerms from "./Misc/NftTerms";
import EpidemicRedirect from "./Profile/EpidemicRedirect";
import ZoomRedirect from "./Profile/ZoomRedirect";
import {CustomizeAi} from "./CustomizeAi/CustomizeAi";
import Conversations from "./Conversations/Conversations";
import Assistants from "./Assistants/Assistants";
import RecordingSessions from "./Recordings/RecordingSessions";

export interface UserInfoProps {
  role: string,
  employees: string,
  gamerReason: string
}

interface AppProps {
  cognitoUser: any,
  userInfo: UserInfoProps
}

const AuthenticatedApp: FC<AppProps> = ({cognitoUser, userInfo}) => {
  const [realvrUser, setRealvrUser] = React.useState<any>({});
  const [fetchingRealvrUser, setFetchingRealvrUser] = React.useState<boolean>(false);
  const [error, setError] = React.useState<any>(null);

  const isCognitoUser = (): boolean => true

  // This could return false if we were unable to reach the server
  const isRealvrUser = (): boolean => {
    return !fetchingRealvrUser && realvrUser.user;
  }

  const isRealvrUserSetup = (): boolean => {
    return isRealvrUser() && (realvrUser as any).user.deviceLinked;
  }

  const getRealvrUser = async () => {
    if (fetchingRealvrUser) {
      return;
    }
    // console.error('fetching', new Date());
    return getUser()
      .then(response => {
        setFetchingRealvrUser(false);
        if (response.ok) {
          return response.json()
        } else {
          setError({message: "Unable to contact server, please try again", title: "!"})
        }
      }).then(json => {
        setRealvrUser(json);
      });
  }
  useEffect(() => {
    getRealvrUser()
      .then(() => {
        if (userInfo.role !== "" && userInfo.employees !== "") {
          postUser({
            role: userInfo.role,
            employees: userInfo.employees,
            gamerReason: userInfo.gamerReason
          }).then().catch();
        }
      })
      .catch(e => {
        console.error({e})
      });
  }, []);

  if (fetchingRealvrUser) {
    return (
      <Router>
        <Switch>
          <Route path="/terms">
            <TermsOfUse isCognitoUser={isCognitoUser()} cognitoUser={cognitoUser}/>;
          </Route>
          <Route path="/privacy">
            <PrivacyPolicy isCognitoUser={isCognitoUser()} cognitoUser={cognitoUser}/>;
          </Route>
          <Route>
            <LoadingScreen/>
          </Route>
        </Switch>
      </Router>
    )
  }

  if (error) {
    return (
      <Router>
        <Switch>
          <Route path="/terms">
            <TermsOfUse isCognitoUser={isCognitoUser()} cognitoUser={cognitoUser}/>;
          </Route>
          <Route path="/privacy">
            <PrivacyPolicy isCognitoUser={isCognitoUser()} cognitoUser={cognitoUser}/>;
          </Route>
          <Route>
            <ErrorMessage title={error?.title} message={error?.message} cognitoUser={cognitoUser}/>
          </Route>
        </Switch>
      </Router>
    )
  }

  function userSetupWrapper(component: any) {
    if (isRealvrUserSetup()) {
      return component;
    } else {
      return <Redirect to="/setup"/>;
    }
  }

  function isUserSuperAdmin() {
    return !!(realvrUser && realvrUser.user && realvrUser.user.isSuperAdmin);
  }

  function userIsAdminWrapper(component: any) {
    if (isRealvrUserSetup() && isUserSuperAdmin()) {
      return component;
    } else {
      return <Redirect to="/"/>;
    }
  }

  return (
    <Router>
      <Switch>
        <Route path="/home" render={() => {
          const orgId = realvrUser.user.orgs[0].id
          const path = `/org/${orgId}/villas`;
          return <Redirect to={path}/>;
        }}/>
        <Route path="/my/villas" render={() =>
          userSetupWrapper(<Home cognitoUser={cognitoUser} realvrUser={realvrUser} loadData={getRealvrUser}/>)
        }/>
        <Route path="/library" render={() =>
          userSetupWrapper(<Library cognitoUser={cognitoUser} loadData={getRealvrUser}/>)
        }/>
        <Route path="/customize_ai" render={() =>
          userSetupWrapper(<CustomizeAi cognitoUser={cognitoUser}/>)
        }/>
        <Route path="/performances" render={() =>
          userSetupWrapper(<Performances cognitoUser={cognitoUser}/>)
        }/>
        <Route path="/recordings" render={() =>
          userSetupWrapper(<RecordingSessions cognitoUser={cognitoUser}/>)
        }/>
        <Route path="/org/:orgId/villas" render={() => {
          return userSetupWrapper(<Home cognitoUser={cognitoUser} realvrUser={realvrUser} loadData={getRealvrUser}/>)
        }}/>
        <Route path="/org/:orgId/team/:teamId/assets" render={() =>
          userSetupWrapper(<TeamAssets cognitoUser={cognitoUser} realvrUser={realvrUser}/>)
        }/>
        <Route path="/org/:orgId/team/:teamId" render={() =>
          userSetupWrapper(<TeamView cognitoUser={cognitoUser} realvrUser={realvrUser} loadData={getRealvrUser}/>)
        }/>
        <Route path="/org/:orgId" render={() => {
          return userSetupWrapper(<OrgView cognitoUser={cognitoUser} realvrUser={realvrUser} loadData={getRealvrUser}/>)
        }}
        />
        <Route path="/setup/link_device" render={() =>
          userSetupWrapper(<LinkDeviceWrapper cognitoUser={cognitoUser} loadData={getRealvrUser}/>)
        }/>
        <Route path="/qa" render={() =>
          userSetupWrapper(<QuestionAnswer isCognitoUser={isCognitoUser()} cognitoUser={cognitoUser}/>)
        }/>
        <Route path="/setup" render={() => {
          if (isRealvrUserSetup()) {
            return <Redirect to="/home"/>
          } else {
            if (isRealvrUser()) {
              return <ProfileWizard cognitoUser={cognitoUser} realvrUser={realvrUser}
                                    getRealvrUser={getRealvrUser}/>
            } else {
              return <Redirect to="/"/>
            }
          }
        }}/>
        <Route path="/admin" render={() => {
          return userIsAdminWrapper(<Admin cognitoUser={cognitoUser}/>);
        }}/>;
        <Route path="/apps" render={() => {
          return userSetupWrapper(<AppIntegrations cognitoUser={cognitoUser}/>)
        }}/>
        <Route path="/assistants" render={() => {
          return userSetupWrapper(<Assistants cognitoUser={cognitoUser}/>)
        }}/>
        <Route path="/conversations" render={() => {
          return userSetupWrapper(<Conversations cognitoUser={cognitoUser}/>)
        }}/>
        <Route path="/sketchfab/redirect">
          <SketchfabRedirect/>
        </Route>
        <Route path="/epidemic/redirect">
          <EpidemicRedirect/>
        </Route>
        <Route path="/zoom/redirect">
          <ZoomRedirect/>
        </Route>
        <Route path="/terms" render={() => {
          return <TermsOfUse isCognitoUser={isCognitoUser()} cognitoUser={cognitoUser}/>;
        }}/>
        <Route path="/nft_terms" render={() => {
          return <NftTerms isCognitoUser={isCognitoUser()} cognitoUser={cognitoUser}/>;
        }}/>
        <Route path="/privacy" render={() => {
          return <PrivacyPolicy isCognitoUser={isCognitoUser()} cognitoUser={cognitoUser}/>;
        }}/>
        <Route path="/" render={() => {
          if (isRealvrUserSetup()) {
            return <Redirect to="/home"/>
            // return <Redirect to="/org/a1287306-708f-40e0-819f-2e56f9618c62/team/0563c06f-68b7-4b7f-a254-385874698c19"/>
            // return <Redirect to="/org/fb4195f9-051e-44c8-976b-a68cc1f0e8b3/team/8d38533d-19eb-4b24-8d6a-7070fd7cac35/assets/"/>
          } else if (isRealvrUser()) {
            return <Redirect to="/setup"/>
          } else {
            return <ErrorMessage title={error?.title} message={error?.message} cognitoUser={cognitoUser}/>
          }
        }}/>
      </Switch>
    </Router>
  );
}

export default AuthenticatedApp;
